// src/admin/presets/index.tsx - UPDATED WITH CONFIGURATION
import React, { useState, useEffect } from 'react';
import PresetReviewList from './PresetReviewList';
import { api } from './api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { 
  Settings, 
  Search, 
  Key, 
  Plus, 
  X, 
  Save,
  RefreshCw 
} from 'lucide-react';

/**
 * Panel administracyjny do zarządzania presetami
 */
const PresetsAdminPanel: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [harvestingStatus, setHarvestingStatus] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('candidates');
  const [showConfig, setShowConfig] = useState(false);
  
  // Konfiguracja
  const [braveApiKey, setBraveApiKey] = useState('');
  const [customQueries, setCustomQueries] = useState<string[]>([]);
  const [newQuery, setNewQuery] = useState('');
  const [harvestLimit, setHarvestLimit] = useState(20);

  // Pobierz statystyki
  useEffect(() => {
    loadStats();
    loadConfig();
  }, []);

  const loadStats = async () => {
    try {
      const data = await api.getStats();
      setStats(data);
    } catch (error) {
      console.error('Błąd pobierania statystyk:', error);
    }
  };

  const loadConfig = async () => {
    try {
      // Pobierz konfigurację z backend
      const response = await fetch('https://api.plexisystem.pl/api/presets/config');
      if (response.ok) {
        const config = await response.json();
        console.log('[Frontend] Loaded config:', config);
        
        // Ustaw klucz API (jeśli jest pełny, nie tylko maskowany)
        if (config.brave_api_key && !config.brave_api_key.startsWith('***')) {
          setBraveApiKey(config.brave_api_key);
        } else if (config.brave_api_key) {
          // Jeśli maskowany, pozostaw puste pole dla edycji
          setBraveApiKey('');
        }
        
        // Ustaw zapytania
        setCustomQueries(config.queries || []);
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const saveConfig = async () => {
    try {
      console.log('[Frontend] Saving config:', { 
        brave_api_key: braveApiKey ? '***' : 'empty',
        queries: customQueries 
      });
      
      const response = await fetch('https://api.plexisystem.pl/api/presets/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brave_api_key: braveApiKey,
          queries: customQueries
        })
      });
      
      const result = await response.json();
      console.log('[Frontend] Save result:', result);
      
      if (response.ok && result.success) {
        setSuccess(`Konfiguracja zapisana! (${result.updated?.join(', ') || 'OK'})`);
        setTimeout(() => {
          setSuccess(null);
          setShowConfig(false);
        }, 3000);
      } else {
        setError(result.error || 'Błąd zapisu konfiguracji');
      }
    } catch (error) {
      console.error('[Frontend] Save error:', error);
      setError('Błąd zapisu konfiguracji');
    }
  };

  // Uruchom harvestowanie
  const startHarvesting = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    setHarvestingStatus('Rozpoczynam harvestowanie...');

    try {
      // Użyj custom queries lub domyślne
      const queries = customQueries.length > 0 ? customQueries : [
        'ekspozytor schodkowy plexi',
        'pojemnik z plexi bez dna',
        'kaseton reklamowy dibond',
        'gablota z plexi z zamkiem'
      ];

      const result = await api.startHarvest(queries, harvestLimit);
      
      if (result.success) {
        setSuccess(`Harvestowanie zakończone! Znaleziono ${result.stats?.saved || 0} nowych presetów`);
        setHarvestingStatus(null);
        loadStats();
      } else {
        setError(result.error || 'Błąd harvestowania');
        setHarvestingStatus(null);
      }
    } catch (error: any) {
      setError(`Błąd harvestowania: ${error.message}`);
      setHarvestingStatus(null);
    } finally {
      setLoading(false);
    }
  };

  const addQuery = () => {
    if (newQuery.trim()) {
      setCustomQueries([...customQueries, newQuery.trim()]);
      setNewQuery('');
    }
  };

  const removeQuery = (index: number) => {
    setCustomQueries(customQueries.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            🎯 Preset Harvester
          </h1>
          <p className="text-gray-400">
            Automatyczne zbieranie i analiza produktów konkurencji
          </p>
        </div>

        {/* Statystyki */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-black/40 backdrop-blur-xl rounded-xl border border-white/10 p-4">
              <div className="text-3xl font-bold text-blue-400">{stats.total || 0}</div>
              <div className="text-sm text-gray-400">Wszystkie kandydaty</div>
            </div>
            <div className="bg-black/40 backdrop-blur-xl rounded-xl border border-white/10 p-4">
              <div className="text-3xl font-bold text-yellow-400">{stats.pending || 0}</div>
              <div className="text-sm text-gray-400">Do przeglądu</div>
            </div>
            <div className="bg-black/40 backdrop-blur-xl rounded-xl border border-white/10 p-4">
              <div className="text-3xl font-bold text-green-400">{stats.accepted || 0}</div>
              <div className="text-sm text-gray-400">Zaakceptowane</div>
            </div>
            <div className="bg-black/40 backdrop-blur-xl rounded-xl border border-white/10 p-4">
              <div className="text-3xl font-bold text-red-400">{stats.rejected || 0}</div>
              <div className="text-sm text-gray-400">Odrzucone</div>
            </div>
            <div className="bg-black/40 backdrop-blur-xl rounded-xl border border-white/10 p-4">
              <div className="text-3xl font-bold text-purple-400">{stats.presets || 0}</div>
              <div className="text-sm text-gray-400">Aktywne presety</div>
            </div>
          </div>
        )}

        {/* Panel konfiguracji */}
        {showConfig && (
          <div className="bg-black/40 backdrop-blur-xl rounded-xl border border-violet-500/30 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Konfiguracja Harvestera
              </h2>
              <button
                onClick={() => setShowConfig(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Brave API Key */}
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-300 mb-2 block">
                <Key className="w-4 h-4 inline mr-1" />
                Brave Search API Key
              </label>
              <input
                type="password"
                value={braveApiKey}
                onChange={(e) => setBraveApiKey(e.target.value)}
                placeholder="BSAXrhVtWmdmJqlbraUdaa10-cJuu1u"
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-violet-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Klucz API do Brave Search - potrzebny do wyszukiwania produktów
              </p>
            </div>

            {/* Custom Queries */}
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-300 mb-2 block">
                <Search className="w-4 h-4 inline mr-1" />
                Frazy wyszukiwania
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newQuery}
                  onChange={(e) => setNewQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addQuery()}
                  placeholder="np. ekspozytor plexi schodkowy"
                  className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-violet-500"
                />
                <button
                  onClick={addQuery}
                  className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-1">
                {customQueries.map((query, index) => (
                  <div key={index} className="flex items-center gap-2 bg-white/5 px-3 py-1 rounded">
                    <span className="flex-1 text-sm text-gray-300">{query}</span>
                    <button
                      onClick={() => removeQuery(index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Limit */}
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-300 mb-2 block">
                Limit wyników na harvestowanie
              </label>
              <input
                type="number"
                value={harvestLimit}
                onChange={(e) => setHarvestLimit(parseInt(e.target.value) || 20)}
                min="5"
                max="100"
                className="w-32 px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-violet-500"
              />
            </div>

            {/* Save button */}
            <button
              onClick={saveConfig}
              className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Zapisz konfigurację
            </button>
          </div>
        )}

        {/* Akcje */}
        <div className="bg-black/40 backdrop-blur-xl rounded-xl border border-white/10 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white mb-2">Harvestowanie</h2>
              <p className="text-gray-400">
                Uruchom proces wyszukiwania i ekstrakcji nowych presetów z internetu
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowConfig(!showConfig)}
                className="px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/20 flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                Konfiguracja
              </button>
              <button
                onClick={startHarvesting}
                disabled={loading}
                className="px-6 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-lg hover:from-violet-700 hover:to-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Przetwarzanie...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    Uruchom Harvestowanie
                  </>
                )}
              </button>
              <button
                onClick={loadStats}
                className="px-4 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/20 flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Odśwież
              </button>
            </div>
          </div>

          {harvestingStatus && (
            <div className="mt-4 bg-violet-500/10 border border-violet-500/30 rounded-lg p-4">
              <div className="flex items-center text-violet-300">
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                {harvestingStatus}
              </div>
            </div>
          )}
        </div>

        {/* Komunikaty */}
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-4">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-500/10 border border-green-500/30 text-green-400 rounded-lg p-4">
            {success}
          </div>
        )}

        {/* Tabs z listami */}
        <div className="bg-black/40 backdrop-blur-xl rounded-xl border border-white/10 p-6">
          <div>
            <div className="flex space-x-4 mb-4 border-b border-white/10">
              <button
                onClick={() => setActiveTab('candidates')}
                className={`pb-2 px-1 ${activeTab === 'candidates' ? 'border-b-2 border-violet-500 text-violet-400' : 'text-gray-400'}`}
              >
                Do przeglądu ({stats?.pending || 0})
              </button>
              <button
                onClick={() => setActiveTab('accepted')}
                className={`pb-2 px-1 ${activeTab === 'accepted' ? 'border-b-2 border-violet-500 text-violet-400' : 'text-gray-400'}`}
              >
                Zaakceptowane ({stats?.accepted || 0})
              </button>
              <button
                onClick={() => setActiveTab('rejected')}
                className={`pb-2 px-1 ${activeTab === 'rejected' ? 'border-b-2 border-violet-500 text-violet-400' : 'text-gray-400'}`}
              >
                Odrzucone ({stats?.rejected || 0})
              </button>
              <button
                onClick={() => setActiveTab('presets')}
                className={`pb-2 px-1 ${activeTab === 'presets' ? 'border-b-2 border-violet-500 text-violet-400' : 'text-gray-400'}`}
              >
                Aktywne presety ({stats?.presets || 0})
              </button>
            </div>

            {activeTab === 'candidates' && (
              <PresetReviewList 
                status="pending" 
                onUpdate={loadStats}
              />
            )}

            {activeTab === 'accepted' && (
              <PresetReviewList 
                status="accepted" 
                onUpdate={loadStats}
                readOnly
              />
            )}

            {activeTab === 'rejected' && (
              <PresetReviewList 
                status="rejected" 
                onUpdate={loadStats}
                readOnly
              />
            )}

            {activeTab === 'presets' && (
              <div className="text-center py-8 text-gray-500">
                Lista aktywnych presetów - w przygotowaniu
              </div>
            )}
          </div>
        </div>

        {/* Footer z informacją */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            ⚠️ Wszystkie dane pobrane z internetu są oznaczone jako "research-only" 
            i nie mogą być używane komercyjnie bez weryfikacji praw autorskich.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PresetsAdminPanel;
